import { type ComponentType, createContext, type ReactNode, useContext } from 'react';

interface TriggerProps {
    floating?: boolean;
    className?: string;
}

const ModalFeedbackContext = createContext<ComponentType<TriggerProps> | null>(null);

/** Makes the feedback trigger available to every dialog and sheet below it. */
export function ModalFeedbackProvider({
    trigger,
    children,
}: {
    trigger: ComponentType<TriggerProps>;
    children: ReactNode;
}) {
    return (
        <ModalFeedbackContext.Provider value={trigger}>{children}</ModalFeedbackContext.Provider>
    );
}

/**
 * Feedback trigger rendered by the modal surfaces themselves, so a dialog or a
 * drawer covering the app header still has one. Renders nothing when no trigger
 * is provided (component tests, isolated stories).
 *
 * It sits beside the modal's close button: that is the one strip every modal
 * already keeps clear, so a shared primitive can put something there without
 * landing on content. Modals that reserve room for their own header controls
 * reserve `pr-18` for the pair. It is rendered after the close button, so it
 * never becomes the first tabbable node and steals the focus Radix gives the
 * modal on open.
 *
 * Indirection through context on purpose: the widget builds on `ui/dialog`, so
 * importing it from there would be a cycle.
 */
export function ModalFeedbackTrigger() {
    const Trigger = useContext(ModalFeedbackContext);
    if (!Trigger) return null;
    return <Trigger className="absolute top-2.5 right-10 z-[60]" />;
}
